import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { useUser } from "../context/UserContext";
import { deleteCookie } from "../utils/common";

/**
 * Tear down the session and return to the landing page.
 *
 * Extracted from NavBar, which owned the only copy. A session lives in three places at
 * once — React context, sessionStorage, and two cookies — so "log out" is not one call and
 * a second hand-rolled copy would inevitably miss one of them and leave the user half
 * signed-in. Account deletion needs exactly this teardown once the server has closed the
 * account, which is what forced the extraction.
 *
 * `deleteCookie` clears both the host-only and the `Domain=`-scoped cookie; dropping only
 * one leaves the other readable and logout silently does nothing.
 *
 * The `logout` window event is what page-level caches (project tab state in localStorage,
 * see ProjectList / BV_PhaseList / OverallSales) listen for to drop per-user state.
 */
export default function useLogout() {
  const navigate = useNavigate();
  const { setUserInfo } = useUser();

  return useCallback(() => {
    window.dispatchEvent(new Event("logout"));

    setUserInfo(null);
    sessionStorage.removeItem("userInfo");
    sessionStorage.removeItem("authToken");
    deleteCookie("_USER_AUTH_", { path: "/" });
    deleteCookie("accessToken", { path: "/" });
    // With `persona` now undefined, "/" renders the unauthenticated route block.
    navigate("/");
  }, [navigate, setUserInfo]);
}
